import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { VoiceNoteInput } from '../../components/ui/voice-note-input';
import {
  Plus, Pencil, Trash2, Loader2, BookOpen, Search,
  Shield, AlertTriangle, ChevronDown, ChevronUp, Save, X, Tag,
  Lightbulb, ArrowRightLeft, PlusCircle, MinusCircle, Check, XCircle, TrendingUp,
  Pill, Package, Link2, Sparkles, Scissors, Activity
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function SkuPicker({ selectedSkus, onChange }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!searchQuery.trim() || searchQuery.length < 2) { setResults([]); return; }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await axios.get(`${API_URL}/api/pharmacy/medicines`, {
          params: { q: searchQuery, limit: 10 },
          withCredentials: true
        });
        setResults(data.items || []);
      } catch (e) { console.error(e); }
      finally { setSearching(false); }
    }, 250);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const addSku = (med) => {
    if (selectedSkus.some(s => s.sku === med.sku)) return;
    onChange([...selectedSkus, { sku: med.sku, name: med.name, company: med.company }]);
    setSearchQuery('');
    setResults([]);
  };

  const removeSku = (sku) => {
    onChange(selectedSkus.filter(s => s.sku !== sku));
  };

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search pharmacy by name, company, or SKU..."
          className="pl-8 text-xs h-9 rounded-[14px]"
          data-testid="sku-picker-search"
        />
        {searching && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 animate-spin text-muted-foreground" />}
      </div>
      {results.length > 0 && (
        <div className="border rounded-[14px] max-h-[150px] overflow-y-auto bg-white" data-testid="sku-picker-results">
          {results.map(med => (
            <button
              key={med.sku}
              onClick={() => addSku(med)}
              disabled={selectedSkus.some(s => s.sku === med.sku)}
              className="w-full text-left px-3 py-2 text-xs hover:bg-[#E6EFEA]/50 flex items-center justify-between border-b last:border-b-0 disabled:opacity-40"
            >
              <div>
                <span className="font-medium">{med.name}</span>
                <span className="text-muted-foreground ml-2">{med.company} · {med.sku}</span>
              </div>
              {!med.in_stock && <Badge variant="outline" className="text-[8px] border-red-200 text-red-500">Out of stock</Badge>}
            </button>
          ))}
        </div>
      )}
      {selectedSkus.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selectedSkus.map(s => (
            <Badge key={s.sku} variant="outline" className="gap-1 text-[10px] py-1 px-2 border-[rgba(14,81,50,0.15)] bg-[#E6EFEA]/50">
              <Pill className="w-2.5 h-2.5 text-[#0E5132]" />
              {s.name}
              <button onClick={() => removeSku(s.sku)} className="ml-0.5 hover:text-red-500">
                <X className="w-2.5 h-2.5" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}



function TagInput({ tags, onChange, placeholder }) {
  const [input, setInput] = useState('');
  const handleKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === ',') && input.trim()) {
      e.preventDefault();
      const newTag = input.trim().toLowerCase();
      if (!tags.includes(newTag)) onChange([...tags, newTag]);
      setInput('');
    }
    if (e.key === 'Backspace' && !input && tags.length) {
      onChange(tags.slice(0, -1));
    }
  };
  return (
    <div className="flex flex-wrap gap-1.5 p-2 border border-slate-200 rounded-lg bg-white min-h-[38px]">
      {tags.map(t => (
        <Badge key={t} variant="secondary" className="gap-1 text-[10px] bg-blue-50 text-blue-700 border border-blue-200">
          {t}
          <button type="button" onClick={() => onChange(tags.filter(x => x !== t))} className="ml-0.5 hover:text-red-600"><X className="w-2.5 h-2.5" /></button>
        </Badge>
      ))}
      <input
        value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown}
        placeholder={tags.length === 0 ? placeholder : ''} className="flex-1 min-w-[100px] text-xs outline-none bg-transparent"
      />
    </div>
  );
}

function ProtocolFormModal({ open, onClose, onSave, protocol }) {
  const isEdit = !!protocol;
  const [form, setForm] = useState({
    condition: '', condition_tags: [], severity: 'all',
    recommended_treatments: [], contraindications: [], alternatives: [],
    preferred_medicines: [], tagged_skus: [], procedure_guidelines: '', medicine_usage_rules: '', notes: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && protocol) {
      setForm({
        condition: protocol.condition || '',
        condition_tags: protocol.condition_tags || [],
        severity: protocol.severity || 'all',
        recommended_treatments: protocol.recommended_treatments || [],
        contraindications: protocol.contraindications || [],
        alternatives: protocol.alternatives || [],
        preferred_medicines: protocol.preferred_medicines || [],
        tagged_skus: protocol.tagged_skus || [],
        procedure_guidelines: protocol.procedure_guidelines || '',
        medicine_usage_rules: protocol.medicine_usage_rules || '',
        notes: protocol.notes || '',
      });
    } else if (open) {
      setForm({
        condition: '', condition_tags: [], severity: 'all',
        recommended_treatments: [], contraindications: [], alternatives: [],
        preferred_medicines: [], tagged_skus: [], procedure_guidelines: '', medicine_usage_rules: '', notes: ''
      });
    }
  }, [open, protocol]);

  const handleSave = async () => {
    if (!form.condition.trim()) { toast.error('Condition name is required'); return; }
    if (form.condition_tags.length === 0) { toast.error('At least one condition tag is required'); return; }
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="protocol-form-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <BookOpen className="w-4 h-4" /> {isEdit ? 'Edit Protocol' : 'New Protocol'}
          </DialogTitle>
          <DialogDescription>Define treatment protocols for conditions. The AI agent uses these during prescription generation.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <VoiceNoteInput
            endpoint="/api/knowledge-base/voice-enrich"
            context={{ form_type: 'protocol', existing_form: form }}
            onResult={(extracted) => {
              setForm(prev => ({
                ...prev,
                condition: extracted.condition || prev.condition,
                severity: extracted.severity || prev.severity,
                condition_tags: extracted.condition_tags?.length ? extracted.condition_tags : prev.condition_tags,
                recommended_treatments: extracted.recommended_treatments?.length ? extracted.recommended_treatments : prev.recommended_treatments,
                contraindications: extracted.contraindications?.length ? extracted.contraindications : prev.contraindications,
                alternatives: extracted.alternatives?.length ? extracted.alternatives : prev.alternatives,
                preferred_medicines: extracted.preferred_medicines?.length ? extracted.preferred_medicines : prev.preferred_medicines,
                procedure_guidelines: extracted.procedure_guidelines || prev.procedure_guidelines,
                medicine_usage_rules: extracted.medicine_usage_rules || prev.medicine_usage_rules,
                notes: extracted.notes || prev.notes,
              }));
            }}
            label="Dictate Protocol"
          />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-slate-700">Condition Name *</label>
              <Input value={form.condition} onChange={e => setForm(p => ({ ...p, condition: e.target.value }))}
                placeholder="e.g., Acne Vulgaris" className="mt-1 text-xs" data-testid="protocol-condition-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">Severity</label>
              <select value={form.severity} onChange={e => setForm(p => ({ ...p, severity: e.target.value }))}
                className="mt-1 w-full h-9 px-2 text-xs border border-slate-200 rounded-md bg-white" data-testid="protocol-severity-select">
                <option value="all">All Severities</option>
                <option value="mild">Mild</option>
                <option value="moderate">Moderate</option>
                <option value="severe">Severe</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-700">Condition Tags * <span className="text-slate-400 font-normal">(press Enter to add)</span></label>
            <div className="mt-1"><TagInput tags={form.condition_tags} onChange={t => setForm(p => ({ ...p, condition_tags: t }))} placeholder="e.g., acne, pimples..." /></div>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-700">Recommended Treatments</label>
            <div className="mt-1"><TagInput tags={form.recommended_treatments} onChange={t => setForm(p => ({ ...p, recommended_treatments: t }))} placeholder="e.g., Benzoyl Peroxide 2.5%..." /></div>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-700 text-red-700">Contraindications</label>
            <div className="mt-1"><TagInput tags={form.contraindications} onChange={t => setForm(p => ({ ...p, contraindications: t }))} placeholder="e.g., Isotretinoin in pregnancy..." /></div>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-700">Alternatives</label>
            <div className="mt-1"><TagInput tags={form.alternatives} onChange={t => setForm(p => ({ ...p, alternatives: t }))} placeholder="e.g., Salicylic acid wash..." /></div>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-700">Preferred Medicines (free text)</label>
            <div className="mt-1"><TagInput tags={form.preferred_medicines} onChange={t => setForm(p => ({ ...p, preferred_medicines: t }))} placeholder="Generic medicines not in pharmacy..." /></div>
          </div>

          <div>
            <label className="text-xs font-medium text-[#0E5132] flex items-center gap-1.5">
              <Package className="w-3.5 h-3.5" /> Tagged Pharmacy SKUs
            </label>
            <p className="text-[10px] text-muted-foreground mb-1">Link specific medicines from pharmacy inventory to this protocol</p>
            <SkuPicker selectedSkus={form.tagged_skus} onChange={skus => setForm(p => ({ ...p, tagged_skus: skus }))} />
          </div>

          <div>
            <label className="text-xs font-medium text-slate-700">Procedure Guidelines</label>
            <Textarea value={form.procedure_guidelines} onChange={e => setForm(p => ({ ...p, procedure_guidelines: e.target.value }))}
              className="mt-1 text-xs min-h-[50px]" placeholder="When to recommend procedures..." />
          </div>

          <div>
            <label className="text-xs font-medium text-slate-700">Medicine Usage Rules</label>
            <Textarea value={form.medicine_usage_rules} onChange={e => setForm(p => ({ ...p, medicine_usage_rules: e.target.value }))}
              className="mt-1 text-xs min-h-[50px]" placeholder="Dosing rules, duration limits..." />
          </div>

          <div>
            <label className="text-xs font-medium text-slate-700">Notes</label>
            <Textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
              className="mt-1 text-xs min-h-[40px]" placeholder="Additional notes..." />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="rounded-full bg-blue-600 hover:bg-blue-700 gap-1.5" data-testid="save-protocol-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isEdit ? 'Update Protocol' : 'Create Protocol'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProtocolCard({ protocol, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try { await onDelete(protocol.id); } catch { setDeleting(false); }
  };

  const severityColor = {
    mild: 'bg-green-50 text-green-700 border-green-200',
    moderate: 'bg-amber-50 text-amber-700 border-amber-200',
    severe: 'bg-red-50 text-red-700 border-red-200',
    all: 'bg-slate-50 text-slate-600 border-slate-200',
  };

  return (
    <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-testid={`protocol-card-${protocol.id}`}>
      <CardHeader className="pb-2 pt-3 px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-blue-600" />
            <CardTitle className="text-sm font-heading">{protocol.condition}</CardTitle>
            <Badge variant="outline" className={`text-[10px] ${severityColor[protocol.severity] || severityColor.all}`}>
              {protocol.severity}
            </Badge>
            {protocol.is_draft && (
              <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200 animate-pulse">
                Draft — Needs Review
              </Badge>
            )}
            <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-500">v{protocol.version}</Badge>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" onClick={() => onEdit(protocol)} className="h-7 w-7 text-slate-400 hover:text-blue-600" data-testid={`edit-protocol-${protocol.id}`}>
              <Pencil className="w-3.5 h-3.5" />
            </Button>
            <Button variant="ghost" size="icon" onClick={handleDelete} disabled={deleting} className="h-7 w-7 text-slate-400 hover:text-red-600" data-testid={`delete-protocol-${protocol.id}`}>
              {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setExpanded(!expanded)} className="h-7 w-7 text-slate-400">
              {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </Button>
          </div>
        </div>
        <div className="flex flex-wrap gap-1 mt-1">
          {protocol.condition_tags?.map(t => (
            <Badge key={t} variant="secondary" className="text-[9px] bg-blue-50 text-blue-600 border border-blue-100">
              <Tag className="w-2 h-2 mr-0.5" />{t}
            </Badge>
          ))}
        </div>
      </CardHeader>
      {expanded && (
        <CardContent className="px-4 pb-3 space-y-2 border-t border-slate-100 pt-2">
          {protocol.recommended_treatments?.length > 0 && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-600">Recommended Treatments</p>
              <div className="flex flex-wrap gap-1 mt-0.5">{protocol.recommended_treatments.map(t => <Badge key={t} variant="outline" className="text-[10px] border-emerald-200 text-emerald-700">{t}</Badge>)}</div>
            </div>
          )}
          {protocol.contraindications?.length > 0 && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-red-600 flex items-center gap-1"><AlertTriangle className="w-2.5 h-2.5" /> Contraindications</p>
              <div className="flex flex-wrap gap-1 mt-0.5">{protocol.contraindications.map(t => <Badge key={t} variant="outline" className="text-[10px] border-red-200 text-red-600">{t}</Badge>)}</div>
            </div>
          )}
          {protocol.alternatives?.length > 0 && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Alternatives</p>
              <div className="flex flex-wrap gap-1 mt-0.5">{protocol.alternatives.map(t => <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>)}</div>
            </div>
          )}
          {protocol.tagged_skus?.length > 0 && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#0E5132] flex items-center gap-1"><Package className="w-2.5 h-2.5" /> Pharmacy SKUs</p>
              <div className="flex flex-wrap gap-1 mt-0.5">
                {protocol.tagged_skus.map(s => (
                  <Badge key={s.sku || s} variant="outline" className="text-[10px] border-[rgba(14,81,50,0.15)] bg-[#E6EFEA]/50 text-[#0E5132] gap-0.5">
                    <Pill className="w-2.5 h-2.5" /> {s.name || s.sku || s}
                  </Badge>
                ))}
              </div>
            </div>
          )}
          {protocol.procedure_guidelines && (
            <div><p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Procedure Guidelines</p><p className="text-[11px] text-slate-600 mt-0.5">{protocol.procedure_guidelines}</p></div>
          )}
          {protocol.medicine_usage_rules && (
            <div><p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Medicine Usage Rules</p><p className="text-[11px] text-slate-600 mt-0.5">{protocol.medicine_usage_rules}</p></div>
          )}
          {protocol.notes && (
            <div><p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Notes</p><p className="text-[11px] text-slate-600 mt-0.5">{protocol.notes}</p></div>
          )}
          <div className="text-[9px] text-slate-400 pt-1 border-t border-slate-100">
            Created by {protocol.created_by_name} on {new Date(protocol.created_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}
          </div>
        </CardContent>
      )}
    </Card>
  );
}

function InsightsPanel({ onProtocolUpdated }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(null);
  const [dismissing, setDismissing] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    const fetchSuggestions = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/knowledge-base/insights/suggestions`, { withCredentials: true });
        setSuggestions(data.suggestions || []);
      } catch { /* no suggestions available */ }
      finally { setLoading(false); }
    };
    fetchSuggestions();
  }, []);

  const handleApply = async (suggestion) => {
    setApplying(suggestion);
    try {
      const { data } = await axios.post(`${API_URL}/api/knowledge-base/insights/apply`, { suggestion }, { withCredentials: true });
      if (data.status === 'applied') {
        toast.success(`Protocol updated: ${data.condition} v${data.new_version}`);
        setSuggestions(prev => prev.filter(s => s !== suggestion));
        onProtocolUpdated?.();
      } else {
        toast.error(data.message || 'Could not apply');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to apply'); }
    finally { setApplying(null); }
  };

  const handleDismiss = async (suggestion) => {
    setDismissing(suggestion);
    try {
      await axios.post(`${API_URL}/api/knowledge-base/insights/dismiss`, { suggestion }, { withCredentials: true });
      setSuggestions(prev => prev.filter(s => s !== suggestion));
      toast.info('Suggestion dismissed');
    } catch { toast.error('Failed'); }
    finally { setDismissing(null); }
  };

  if (loading) return null;
  if (suggestions.length === 0) return null;

  const typeIcon = { substitution: ArrowRightLeft, addition: PlusCircle, removal: MinusCircle };
  const typeColor = {
    substitution: 'text-blue-600 bg-blue-50 border-blue-200',
    addition: 'text-emerald-600 bg-emerald-50 border-emerald-200',
    removal: 'text-red-600 bg-red-50 border-red-200',
  };
  const priorityColor = { high: 'bg-red-100 text-red-700', medium: 'bg-amber-100 text-amber-700' };

  return (
    <Card className="border-amber-200 bg-gradient-to-r from-amber-50/50 to-orange-50/30 shadow-sm" data-testid="insights-panel">
      <CardHeader className="pb-1 pt-3 px-4 cursor-pointer" onClick={() => setCollapsed(!collapsed)}>
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-heading flex items-center gap-2 text-amber-800">
            <Lightbulb className="w-4 h-4 text-amber-500" />
            AI Protocol Insights
            <Badge variant="outline" className="text-[10px] bg-amber-100 text-amber-700 border-amber-300">
              {suggestions.length} suggestion{suggestions.length !== 1 ? 's' : ''}
            </Badge>
          </CardTitle>
          <div className="flex items-center gap-1">
            <TrendingUp className="w-3.5 h-3.5 text-amber-500" />
            {collapsed ? <ChevronDown className="w-3.5 h-3.5 text-amber-500" /> : <ChevronUp className="w-3.5 h-3.5 text-amber-500" />}
          </div>
        </div>
        <p className="text-[10px] text-amber-600 mt-0.5">Based on doctor override patterns from past prescriptions</p>
      </CardHeader>
      {!collapsed && (
        <CardContent className="px-4 pb-3 space-y-2">
          {suggestions.map((s, i) => {
            const Icon = typeIcon[s.type] || Lightbulb;
            return (
              <div key={i} className={`p-2.5 rounded-lg border ${typeColor[s.type] || typeColor.substitution}`} data-testid={`suggestion-${i}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-1.5 mb-1">
                      <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="text-[10px] font-bold uppercase tracking-wider">{s.type}</span>
                      <Badge variant="outline" className="text-[9px] bg-white/80">{s.condition_tag}</Badge>
                      <Badge variant="outline" className={`text-[9px] ${priorityColor[s.priority] || priorityColor.medium}`}>{s.priority}</Badge>
                      <span className="text-[9px] text-slate-400">{s.occurrence_count}x</span>
                    </div>
                    <p className="text-[11px] font-medium" dangerouslySetInnerHTML={{ __html: s.description.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
                    <p className="text-[10px] text-slate-500 mt-0.5">{s.action}</p>
                    {s.doctor_reasons?.length > 0 && (
                      <div className="mt-1">
                        <p className="text-[9px] text-slate-400 font-medium">Doctor Reasoning:</p>
                        {s.doctor_reasons.map((r, ri) => <p key={ri} className="text-[9px] text-slate-500 italic">&ldquo;{r}&rdquo;</p>)}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <Button size="icon" variant="ghost" onClick={() => handleApply(s)}
                      disabled={applying === s} className="h-6 w-6 text-emerald-600 hover:bg-emerald-100"
                      title="Apply to protocol" data-testid={`apply-suggestion-${i}`}>
                      {applying === s ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => handleDismiss(s)}
                      disabled={dismissing === s} className="h-6 w-6 text-slate-400 hover:bg-red-50 hover:text-red-600"
                      title="Dismiss" data-testid={`dismiss-suggestion-${i}`}>
                      {dismissing === s ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </CardContent>
      )}
    </Card>
  );
}

function SkuSuggestionsPanel({ onProtocolUpdated }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(null);
  const [dismissing, setDismissing] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    const fetchSkuSuggestions = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/knowledge-base/insights/sku-suggestions`, { withCredentials: true });
        setSuggestions(data.suggestions || []);
      } catch { /* no suggestions */ }
      finally { setLoading(false); }
    };
    fetchSkuSuggestions();
  }, []);

  const handleApply = async (suggestion) => {
    setApplying(suggestion.sku + suggestion.protocol_id);
    try {
      const { data } = await axios.post(`${API_URL}/api/knowledge-base/insights/sku-suggestions/apply`, { suggestion }, { withCredentials: true });
      if (data.status === 'applied') {
        toast.success(`Linked ${data.medicine_name} to ${data.condition}`);
        setSuggestions(prev => prev.filter(s => !(s.sku === suggestion.sku && s.protocol_id === suggestion.protocol_id)));
        onProtocolUpdated?.();
      } else {
        toast.error(data.message || 'Could not apply');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to apply'); }
    finally { setApplying(null); }
  };

  const handleDismiss = async (suggestion) => {
    setDismissing(suggestion.sku + suggestion.protocol_id);
    try {
      await axios.post(`${API_URL}/api/knowledge-base/insights/sku-suggestions/dismiss`, { suggestion }, { withCredentials: true });
      setSuggestions(prev => prev.filter(s => !(s.sku === suggestion.sku && s.protocol_id === suggestion.protocol_id)));
      toast.info('Suggestion dismissed');
    } catch { toast.error('Failed to dismiss'); }
    finally { setDismissing(null); }
  };

  if (loading) return null;
  if (suggestions.length === 0) return null;

  const priorityColor = { high: 'bg-red-100 text-red-700 border-red-200', medium: 'bg-amber-100 text-amber-700 border-amber-200', low: 'bg-slate-100 text-slate-600 border-slate-200' };

  // Group suggestions by protocol
  const grouped = {};
  for (const s of suggestions) {
    const key = s.protocol_id;
    if (!grouped[key]) grouped[key] = { condition: s.protocol_condition, items: [] };
    grouped[key].items.push(s);
  }

  return (
    <Card className="border-[#0E5132]/20 bg-gradient-to-r from-emerald-50/50 to-teal-50/30" data-testid="sku-suggestions-panel">
      <CardHeader className="pb-1 pt-3 px-4 cursor-pointer" onClick={() => setCollapsed(!collapsed)}>
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-heading flex items-center gap-2 text-[#0E5132]">
            <Sparkles className="w-4 h-4 text-emerald-500" />
            AI SKU Tag Suggestions
            <Badge variant="outline" className="text-[10px] bg-emerald-100 text-emerald-700 border-emerald-300">
              {suggestions.length} suggestion{suggestions.length !== 1 ? 's' : ''}
            </Badge>
          </CardTitle>
          <div className="flex items-center gap-1">
            <Link2 className="w-3.5 h-3.5 text-emerald-500" />
            {collapsed ? <ChevronDown className="w-3.5 h-3.5 text-emerald-500" /> : <ChevronUp className="w-3.5 h-3.5 text-emerald-500" />}
          </div>
        </div>
        <p className="text-[10px] text-emerald-600 mt-0.5">Auto-detected pharmacy medicines that should be linked to protocols based on enrichment data, name matching, and doctor patterns</p>
      </CardHeader>
      {!collapsed && (
        <CardContent className="px-4 pb-3 space-y-3">
          {Object.entries(grouped).map(([protocolId, group]) => (
            <div key={protocolId} className="space-y-1.5">
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#0E5132] flex items-center gap-1">
                <BookOpen className="w-2.5 h-2.5" /> {group.condition}
              </p>
              {group.items.map((s) => {
                const key = s.sku + s.protocol_id;
                return (
                  <div key={key} className="p-2.5 rounded-lg border border-emerald-200 bg-white/80" data-testid={`sku-suggestion-${s.sku}-${s.protocol_id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                          <Pill className="w-3.5 h-3.5 text-[#0E5132] flex-shrink-0" />
                          <span className="text-xs font-semibold text-slate-800">{s.medicine_name}</span>
                          <Badge variant="outline" className="text-[9px] border-slate-200 text-slate-500">{s.sku}</Badge>
                          <Badge variant="outline" className={`text-[9px] ${priorityColor[s.priority] || priorityColor.medium}`}>{s.priority}</Badge>
                          {!s.in_stock && <Badge variant="outline" className="text-[8px] border-red-200 text-red-500">Out of stock</Badge>}
                        </div>
                        <p className="text-[10px] text-slate-500">{s.medicine_company}</p>
                        <div className="mt-1 space-y-0.5">
                          {s.reasons.map((r, ri) => (
                            <p key={ri} className="text-[10px] text-emerald-700 flex items-center gap-1">
                              <Check className="w-2.5 h-2.5 flex-shrink-0" /> {r}
                            </p>
                          ))}
                        </div>
                      </div>
                      <div className="flex flex-col gap-1 flex-shrink-0">
                        <Button size="icon" variant="ghost" onClick={() => handleApply(s)}
                          disabled={applying === key} className="h-7 w-7 text-emerald-600 hover:bg-emerald-100"
                          title="Link SKU to protocol" data-testid={`apply-sku-${s.sku}-${s.protocol_id}`}>
                          {applying === key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => handleDismiss(s)}
                          disabled={dismissing === key} className="h-7 w-7 text-slate-400 hover:bg-red-50 hover:text-red-600"
                          title="Dismiss" data-testid={`dismiss-sku-${s.sku}-${s.protocol_id}`}>
                          {dismissing === key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />}
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </CardContent>
      )}
    </Card>
  );
}

const PROCEDURE_CATEGORIES = [
  { value: 'peel', label: 'Chemical Peel' },
  { value: 'laser', label: 'Laser' },
  { value: 'microdermabrasion', label: 'Microdermabrasion' },
  { value: 'injection', label: 'Injection' },
  { value: 'cryotherapy', label: 'Cryotherapy' },
  { value: 'phototherapy', label: 'Phototherapy' },
  { value: 'surgical', label: 'Surgical' },
  { value: 'topical_procedure', label: 'Topical Procedure' },
  { value: 'other', label: 'Other' },
];

function ProcedureFormModal({ open, onClose, onSave, procedure }) {
  const isEdit = !!procedure;
  const [form, setForm] = useState({
    name: '', category: 'other', indications: [], technique: '', aftercare: '',
    contraindications: [], frequency: '', linked_conditions: [], notes: '', allowed_org_ids: []
  });
  const [saving, setSaving] = useState(false);
  const [orgs, setOrgs] = useState([]);

  useEffect(() => {
    if (open) {
      fetch(`${API_URL}/api/organisations`, { credentials: 'include' })
        .then(r => r.ok ? r.json() : []).then(setOrgs).catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    if (open && procedure) {
      setForm({
        name: procedure.name || '',
        category: procedure.category || 'other',
        indications: procedure.indications || [],
        technique: procedure.technique || '',
        aftercare: procedure.aftercare || '',
        contraindications: procedure.contraindications || [],
        frequency: procedure.frequency || '',
        linked_conditions: procedure.linked_conditions || [],
        notes: procedure.notes || '',
        allowed_org_ids: procedure.allowed_org_ids || [],
      });
    } else if (open) {
      setForm({ name: '', category: 'other', indications: [], technique: '', aftercare: '', contraindications: [], frequency: '', linked_conditions: [], notes: '', allowed_org_ids: [] });
    }
  }, [open, procedure]);

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Procedure name is required'); return; }
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="procedure-form-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <Scissors className="w-4 h-4" /> {isEdit ? 'Edit Procedure' : 'New Procedure Template'}
          </DialogTitle>
          <DialogDescription>Define a procedure protocol that doctors can reference and the AI can suggest.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <VoiceNoteInput
            endpoint="/api/knowledge-base/voice-enrich"
            context={{ form_type: 'procedure', existing_form: form }}
            onResult={(extracted) => {
              setForm(prev => ({
                ...prev,
                name: extracted.name || prev.name,
                category: extracted.category || prev.category,
                indications: extracted.indications?.length ? extracted.indications : prev.indications,
                technique: extracted.technique || prev.technique,
                aftercare: extracted.aftercare || prev.aftercare,
                contraindications: extracted.contraindications?.length ? extracted.contraindications : prev.contraindications,
                frequency: extracted.frequency || prev.frequency,
                linked_conditions: extracted.linked_conditions?.length ? extracted.linked_conditions : prev.linked_conditions,
                notes: extracted.notes || prev.notes,
              }));
            }}
            label="Dictate Procedure"
          />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-slate-700">Procedure Name *</label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g., Chemical Peel — Glycolic Acid" className="mt-1 text-xs" data-testid="proc-template-name" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">Category</label>
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                className="mt-1 w-full h-9 px-3 text-xs border border-slate-200 rounded-md bg-white" data-testid="proc-template-category">
                {PROCEDURE_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-700">Indications</label>
            <TagInput tags={form.indications} onChange={v => setForm(f => ({ ...f, indications: v }))} placeholder="When to suggest this procedure (press Enter)" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-700">Technique / Protocol</label>
            <Textarea value={form.technique} onChange={e => setForm(f => ({ ...f, technique: e.target.value }))}
              placeholder="Step-by-step technique: concentration, duration, method..." className="mt-1 text-xs min-h-[80px]" data-testid="proc-template-technique" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-700">Aftercare Instructions</label>
            <Textarea value={form.aftercare} onChange={e => setForm(f => ({ ...f, aftercare: e.target.value }))}
              placeholder="Post-procedure care instructions for the patient..." className="mt-1 text-xs min-h-[60px]" data-testid="proc-template-aftercare" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-slate-700">Contraindications</label>
              <TagInput tags={form.contraindications} onChange={v => setForm(f => ({ ...f, contraindications: v }))} placeholder="When NOT to do this (press Enter)" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">Frequency / Schedule</label>
              <Input value={form.frequency} onChange={e => setForm(f => ({ ...f, frequency: e.target.value }))}
                placeholder="e.g., Every 2 weeks, 4-6 sessions" className="mt-1 text-xs" data-testid="proc-template-frequency" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-700">Linked Conditions</label>
            <TagInput tags={form.linked_conditions} onChange={v => setForm(f => ({ ...f, linked_conditions: v }))} placeholder="Condition tags this applies to (press Enter)" />
            <p className="text-[9px] text-slate-400 mt-0.5">Links this procedure to treatment protocols (e.g., acne, pigmentation)</p>
          </div>
          {orgs.length > 0 && (
            <div>
              <label className="text-xs font-medium text-slate-700">Organisation Access</label>
              <p className="text-[9px] text-slate-400 mb-1.5">Select which organisations can use this procedure in AI prescriptions. Leave empty for all.</p>
              <div className="flex flex-wrap gap-2" data-testid="proc-org-access">
                {orgs.map(o => {
                  const selected = form.allowed_org_ids.includes(o.id);
                  return (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() => {
                        setForm(f => ({
                          ...f,
                          allowed_org_ids: selected
                            ? f.allowed_org_ids.filter(id => id !== o.id)
                            : [...f.allowed_org_ids, o.id]
                        }));
                      }}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                        selected
                          ? 'bg-violet-100 border-violet-300 text-violet-700'
                          : 'bg-white border-slate-200 text-slate-500 hover:border-violet-200'
                      }`}
                      data-testid={`proc-org-toggle-${o.username}`}
                    >
                      {o.name} {selected ? '✓' : ''}
                    </button>
                  );
                })}
              </div>
              {form.allowed_org_ids.length === 0 && (
                <p className="text-[9px] text-emerald-600 mt-1">Available to all organisations</p>
              )}
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-slate-700">Notes</label>
            <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Any additional notes..." className="mt-1 text-xs min-h-[40px]" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full text-xs">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="rounded-full gap-1.5 bg-violet-600 hover:bg-violet-700 text-xs" data-testid="save-procedure-template">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            {isEdit ? 'Update' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProcedureCard({ procedure, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const catLabel = PROCEDURE_CATEGORIES.find(c => c.value === procedure.category)?.label || procedure.category;

  const handleDelete = async () => {
    if (!window.confirm(`Archive "${procedure.name}"?`)) return;
    setDeleting(true);
    try { await onDelete(procedure.id); } catch { toast.error('Delete failed'); }
    finally { setDeleting(false); }
  };

  return (
    <Card className="border-slate-200 hover:border-violet-200 transition-colors" data-testid={`procedure-card-${procedure.id}`}>
      <CardHeader className="py-2.5 px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Scissors className="w-4 h-4 text-violet-500 shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-800 truncate">{procedure.name}</p>
              <div className="flex items-center gap-1.5 mt-0.5">
                <Badge variant="outline" className="text-[9px] bg-violet-50 text-violet-600 border-violet-200">{catLabel}</Badge>
                {procedure.frequency && <span className="text-[9px] text-slate-400">{procedure.frequency}</span>}
                {procedure.allowed_org_ids?.length > 0 ? (
                  <Badge variant="outline" className="text-[8px] border-amber-200 bg-amber-50 text-amber-600">
                    {procedure.allowed_org_ids.length} org{procedure.allowed_org_ids.length > 1 ? 's' : ''}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-[8px] border-emerald-200 bg-emerald-50 text-emerald-600">All orgs</Badge>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" onClick={() => onEdit(procedure)} className="h-7 w-7 text-slate-400 hover:text-violet-600" data-testid={`edit-procedure-${procedure.id}`}>
              <Pencil className="w-3.5 h-3.5" />
            </Button>
            <Button variant="ghost" size="icon" onClick={handleDelete} disabled={deleting} className="h-7 w-7 text-slate-400 hover:text-red-600" data-testid={`delete-procedure-${procedure.id}`}>
              {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setExpanded(!expanded)} className="h-7 w-7 text-slate-400">
              {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </Button>
          </div>
        </div>
        {procedure.indications?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {procedure.indications.map(ind => (
              <Badge key={ind} variant="secondary" className="text-[9px] bg-emerald-50 text-emerald-600 border border-emerald-100">{ind}</Badge>
            ))}
          </div>
        )}
      </CardHeader>
      {expanded && (
        <CardContent className="px-4 pb-3 space-y-2 border-t border-slate-100 pt-2">
          {procedure.technique && (
            <div><p className="text-[10px] font-bold uppercase tracking-wider text-violet-600">Technique / Protocol</p><p className="text-[11px] text-slate-600 mt-0.5 whitespace-pre-wrap">{procedure.technique}</p></div>
          )}
          {procedure.aftercare && (
            <div><p className="text-[10px] font-bold uppercase tracking-wider text-blue-600">Aftercare</p><p className="text-[11px] text-slate-600 mt-0.5 whitespace-pre-wrap">{procedure.aftercare}</p></div>
          )}
          {procedure.contraindications?.length > 0 && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-red-600 flex items-center gap-1"><AlertTriangle className="w-2.5 h-2.5" /> Contraindications</p>
              <div className="flex flex-wrap gap-1 mt-0.5">{procedure.contraindications.map(c => <Badge key={c} variant="outline" className="text-[10px] border-red-200 text-red-600">{c}</Badge>)}</div>
            </div>
          )}
          {procedure.linked_conditions?.length > 0 && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#0E5132] flex items-center gap-1"><Link2 className="w-2.5 h-2.5" /> Linked Conditions</p>
              <div className="flex flex-wrap gap-1 mt-0.5">{procedure.linked_conditions.map(c => <Badge key={c} variant="outline" className="text-[10px] border-emerald-200 text-emerald-700">{c}</Badge>)}</div>
            </div>
          )}
          {procedure.notes && (
            <div><p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Notes</p><p className="text-[11px] text-slate-600 mt-0.5">{procedure.notes}</p></div>
          )}
          <div className="text-[9px] text-slate-400 pt-1 border-t border-slate-100">
            Created by {procedure.created_by_name} on {new Date(procedure.created_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}
            {procedure.updated_by_name && <span> · Updated by {procedure.updated_by_name}</span>}
          </div>
        </CardContent>
      )}
    </Card>
  );
}

function ProcedureLibraryTab() {
  const [procedures, setProcedures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProcedure, setEditingProcedure] = useState(null);

  const fetchProcedures = useCallback(async () => {
    try {
      const params = {};
      if (search) params.q = search;
      if (categoryFilter) params.category = categoryFilter;
      const { data } = await axios.get(`${API_URL}/api/knowledge-base/procedures/list`, { withCredentials: true, params });
      setProcedures(data);
    } catch { toast.error('Failed to load procedures'); }
    finally { setLoading(false); }
  }, [search, categoryFilter]);

  useEffect(() => { fetchProcedures(); }, [fetchProcedures]);

  const handleCreate = async (form) => {
    await axios.post(`${API_URL}/api/knowledge-base/procedures`, form, { withCredentials: true });
    toast.success('Procedure template created');
    fetchProcedures();
  };

  const handleUpdate = async (form) => {
    await axios.put(`${API_URL}/api/knowledge-base/procedures/${editingProcedure.id}`, form, { withCredentials: true });
    toast.success('Procedure template updated');
    fetchProcedures();
  };

  const handleDelete = async (id) => {
    await axios.delete(`${API_URL}/api/knowledge-base/procedures/${id}`, { withCredentials: true });
    toast.success('Procedure archived');
    fetchProcedures();
  };

  return (
    <div className="space-y-4" data-testid="procedure-library-tab">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search procedures..."
            className="pl-8 h-8 text-xs rounded-full" data-testid="procedure-search" />
        </div>
        <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
          className="h-8 px-3 text-xs border border-slate-200 rounded-full bg-white" data-testid="procedure-category-filter">
          <option value="">All Categories</option>
          {PROCEDURE_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <Badge variant="outline" className="text-[10px] bg-violet-50 text-violet-600">{procedures.length} procedure{procedures.length !== 1 ? 's' : ''}</Badge>
        <Button onClick={() => { setEditingProcedure(null); setModalOpen(true); }} className="rounded-full gap-1.5 bg-violet-600 hover:bg-violet-700 text-xs h-8 ml-auto" data-testid="add-procedure-btn">
          <Plus className="w-3.5 h-3.5" /> Add Procedure
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-violet-600" /></div>
      ) : procedures.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-8 text-center space-y-2">
            <Scissors className="w-8 h-8 text-slate-300 mx-auto" />
            <p className="text-sm text-muted-foreground">No procedure templates yet</p>
            <p className="text-xs text-muted-foreground">Add procedure protocols so doctors and AI can reference standard techniques and aftercare</p>
            <Button onClick={() => { setEditingProcedure(null); setModalOpen(true); }} variant="outline" className="rounded-full gap-1 text-xs mt-2" data-testid="empty-add-procedure">
              <Plus className="w-3 h-3" /> Create First Procedure
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {procedures.map(p => (
            <ProcedureCard key={p.id} procedure={p}
              onEdit={(proc) => { setEditingProcedure(proc); setModalOpen(true); }}
              onDelete={handleDelete} />
          ))}
        </div>
      )}

      <ProcedureFormModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditingProcedure(null); }}
        onSave={editingProcedure ? handleUpdate : handleCreate}
        procedure={editingProcedure}
      />
    </div>
  );
}

function KnowledgeBookTab() {
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const generateBook = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/knowledge-base/book`, { withCredentials: true });
      setBook(data);
    } catch { toast.error('Failed to generate book'); }
    finally { setLoading(false); }
  };

  const downloadPDF = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API_URL}/api/knowledge-base/book/pdf`, {
        withCredentials: true, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Eden_Knowledge_Book_${new Date().toISOString().slice(0,10)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch { toast.error('Failed to generate PDF'); }
    finally { setDownloading(false); }
  };

  return (
    <div className="space-y-4" data-testid="knowledge-book-tab">
      <div className="flex items-center gap-2 flex-wrap">
        <Button onClick={generateBook} disabled={loading} className="rounded-full gap-1.5 bg-[#063D2F] hover:bg-[#0E5132] text-xs h-8" data-testid="generate-book-btn">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BookOpen className="w-3.5 h-3.5" />}
          {book ? 'Refresh Book' : 'Generate Knowledge Book'}
        </Button>
        {book && (
          <Button onClick={downloadPDF} disabled={downloading} variant="outline" className="rounded-full gap-1.5 text-xs h-8 border-[#063D2F] text-[#063D2F]" data-testid="download-book-pdf">
            {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Activity className="w-3.5 h-3.5" />}
            Download PDF
          </Button>
        )}
      </div>

      {!book && !loading && (
        <Card className="border-dashed">
          <CardContent className="py-10 text-center space-y-2">
            <BookOpen className="w-10 h-10 text-slate-200 mx-auto" />
            <p className="text-sm text-muted-foreground">Click &ldquo;Generate Knowledge Book&rdquo; to compile all protocols, procedures, and medicine data into one reference</p>
          </CardContent>
        </Card>
      )}

      {loading && (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#063D2F]" /></div>
      )}

      {book && (
        <div className="space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Card className="bg-emerald-50 border-emerald-100"><CardContent className="py-3 px-4 text-center"><p className="text-xl font-bold text-emerald-700">{book.stats.total_protocols}</p><p className="text-[10px] text-emerald-600">Protocols</p></CardContent></Card>
            <Card className="bg-violet-50 border-violet-100"><CardContent className="py-3 px-4 text-center"><p className="text-xl font-bold text-violet-700">{book.stats.total_procedures}</p><p className="text-[10px] text-violet-600">Procedures</p></CardContent></Card>
            <Card className="bg-blue-50 border-blue-100"><CardContent className="py-3 px-4 text-center"><p className="text-xl font-bold text-blue-700">{book.stats.total_medicines}</p><p className="text-[10px] text-blue-600">Medicines</p></CardContent></Card>
            <Card className="bg-amber-50 border-amber-100"><CardContent className="py-3 px-4 text-center"><p className="text-xl font-bold text-amber-700">{book.stats.enriched_medicines}</p><p className="text-[10px] text-amber-600">Enriched</p></CardContent></Card>
          </div>

          {/* Protocols */}
          {book.protocols.length > 0 && (
            <div>
              <h2 className="text-sm font-heading font-bold text-emerald-700 flex items-center gap-1.5 mb-2"><Shield className="w-4 h-4" /> Treatment Protocols ({book.protocols.length})</h2>
              <div className="space-y-2">
                {book.protocols.map((p, i) => (
                  <Card key={i} className="border-emerald-100">
                    <CardContent className="py-3 px-4">
                      <p className="text-sm font-semibold text-slate-800">{p.condition} {p.severity && <Badge variant="outline" className="text-[9px] ml-1">{p.severity}</Badge>}</p>
                      {p.condition_tags?.length > 0 && <div className="flex gap-1 flex-wrap mt-1">{p.condition_tags.map(t => <Badge key={t} variant="secondary" className="text-[9px]">{t}</Badge>)}</div>}
                      {p.recommended_treatments?.length > 0 && <div className="mt-2"><p className="text-[10px] font-bold text-emerald-600">Recommended Treatments</p>{p.recommended_treatments.map((t,j) => <p key={j} className="text-[11px] text-slate-600">• {t}</p>)}</div>}
                      {p.preferred_medicines?.length > 0 && <div className="mt-1.5"><p className="text-[10px] font-bold text-blue-600">Preferred Medicines</p>{p.preferred_medicines.map((m,j) => <p key={j} className="text-[11px] text-slate-600">• {m}</p>)}</div>}
                      {p.contraindications?.length > 0 && <div className="mt-1.5"><p className="text-[10px] font-bold text-red-600">Contraindications</p>{p.contraindications.map((c,j) => <p key={j} className="text-[11px] text-slate-600">• {c}</p>)}</div>}
                      {p.procedure_guidelines && <div className="mt-1.5"><p className="text-[10px] font-bold text-violet-600">Procedure Guidelines</p><p className="text-[11px] text-slate-600 whitespace-pre-wrap">{p.procedure_guidelines}</p></div>}
                      {p.notes && <p className="text-[10px] text-slate-400 mt-1 italic">{p.notes}</p>}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Procedures */}
          {book.procedures.length > 0 && (
            <div>
              <h2 className="text-sm font-heading font-bold text-violet-700 flex items-center gap-1.5 mb-2"><Scissors className="w-4 h-4" /> Procedure Library ({book.procedures.length})</h2>
              <div className="space-y-2">
                {book.procedures.map((p, i) => (
                  <Card key={i} className="border-violet-100">
                    <CardContent className="py-3 px-4">
                      <div className="flex items-center gap-2"><p className="text-sm font-semibold text-slate-800">{p.name}</p><Badge variant="outline" className="text-[9px] bg-violet-50 text-violet-600">{p.category}</Badge></div>
                      {p.frequency && <p className="text-[10px] text-slate-500 mt-0.5">{p.frequency}</p>}
                      {p.indications?.length > 0 && <div className="mt-1.5"><p className="text-[10px] font-bold text-emerald-600">Indications</p><div className="flex gap-1 flex-wrap">{p.indications.map(ind => <Badge key={ind} variant="secondary" className="text-[9px]">{ind}</Badge>)}</div></div>}
                      {p.technique && <div className="mt-1.5"><p className="text-[10px] font-bold text-violet-600">Technique</p><p className="text-[11px] text-slate-600 whitespace-pre-wrap">{p.technique}</p></div>}
                      {p.aftercare && <div className="mt-1.5"><p className="text-[10px] font-bold text-blue-600">Aftercare</p><p className="text-[11px] text-slate-600 whitespace-pre-wrap">{p.aftercare}</p></div>}
                      {p.contraindications?.length > 0 && <div className="mt-1.5"><p className="text-[10px] font-bold text-red-600">Contraindications</p>{p.contraindications.map((c,j) => <p key={j} className="text-[11px] text-slate-600">• {c}</p>)}</div>}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Medicines */}
          {book.medicines.length > 0 && (
            <div>
              <h2 className="text-sm font-heading font-bold text-blue-700 flex items-center gap-1.5 mb-2"><Pill className="w-4 h-4" /> Medicine Inventory ({book.medicines.length})</h2>
              <div className="space-y-1">
                {book.medicines.map((m, i) => {
                  const hasEnrichment = m.usage_notes || m.indications || m.contraindications || m.preferred_combinations;
                  return (
                    <Card key={i} className={`border-slate-100 ${hasEnrichment ? 'border-l-2 border-l-blue-400' : ''}`}>
                      <CardContent className="py-2 px-4">
                        <div className="flex items-center justify-between flex-wrap gap-1">
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-slate-800 truncate">{m.name}</p>
                            <p className="text-[10px] text-slate-400">{[m.company, m.active_ingredients, m.sku].filter(Boolean).join(' · ')}</p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="text-[10px] font-mono text-slate-600">₹{m.mrp}</span>
                            <Badge variant={m.in_stock ? 'default' : 'secondary'} className={`text-[9px] ${m.in_stock ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-500'}`}>{m.in_stock ? `In Stock (${m.stock_qty})` : 'Out'}</Badge>
                          </div>
                        </div>
                        {hasEnrichment && (
                          <div className="mt-1.5 pt-1.5 border-t border-slate-50 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5">
                            {m.usage_notes && <p className="text-[10px] text-slate-600"><span className="font-bold text-blue-600">Use:</span> {m.usage_notes}</p>}
                            {m.indications && <p className="text-[10px] text-slate-600"><span className="font-bold text-emerald-600">For:</span> {m.indications}</p>}
                            {m.contraindications && <p className="text-[10px] text-slate-600"><span className="font-bold text-red-600">Avoid:</span> {m.contraindications}</p>}
                            {m.preferred_combinations && <p className="text-[10px] text-slate-600"><span className="font-bold text-violet-600">Combine:</span> {m.preferred_combinations}</p>}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}

          <p className="text-[9px] text-slate-400 text-center">Generated {new Date(book.generated_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}</p>
        </div>
      )}
    </div>
  );
}

export default function KnowledgeBasePage() {
  const [activeTab, setActiveTab] = useState('protocols');
  const [protocols, setProtocols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProtocol, setEditingProtocol] = useState(null);

  const fetchProtocols = useCallback(async () => {
    try {
      const params = {};
      if (search) params.condition = search;
      const { data } = await axios.get(`${API_URL}/api/knowledge-base`, { withCredentials: true, params });
      setProtocols(data);
    } catch { toast.error('Failed to load protocols'); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { fetchProtocols(); }, [fetchProtocols]);

  const handleCreate = async (form) => {
    await axios.post(`${API_URL}/api/knowledge-base`, form, { withCredentials: true });
    toast.success('Protocol created');
    fetchProtocols();
  };

  const handleUpdate = async (form) => {
    await axios.put(`${API_URL}/api/knowledge-base/${editingProtocol.id}`, form, { withCredentials: true });
    toast.success('Protocol updated (new version)');
    fetchProtocols();
  };

  const handleDelete = async (id) => {
    await axios.delete(`${API_URL}/api/knowledge-base/${id}`, { withCredentials: true });
    toast.success('Protocol archived');
    fetchProtocols();
  };

  const openEdit = (protocol) => { setEditingProtocol(protocol); setModalOpen(true); };
  const openCreate = () => { setEditingProtocol(null); setModalOpen(true); };

  return (
    <div className="space-y-4" data-testid="knowledge-base-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-heading font-bold text-slate-800 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-blue-600" /> Clinic Knowledge Base
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">Treatment protocols and procedure library the AI agent follows</p>
        </div>
        {activeTab === 'protocols' && (
          <Button onClick={openCreate} className="rounded-full gap-1.5 bg-blue-600 hover:bg-blue-700 text-xs h-8" data-testid="add-protocol-btn">
            <Plus className="w-3.5 h-3.5" /> Add Protocol
          </Button>
        )}
      </div>

      {/* Tab Switcher */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-full w-fit" data-testid="kb-tab-switcher">
        <button onClick={() => setActiveTab('protocols')}
          className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all ${activeTab === 'protocols' ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          data-testid="tab-protocols">
          <BookOpen className="w-3 h-3 inline mr-1.5" />Protocols
        </button>
        <button onClick={() => setActiveTab('procedures')}
          className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all ${activeTab === 'procedures' ? 'bg-white text-violet-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          data-testid="tab-procedures">
          <Scissors className="w-3 h-3 inline mr-1.5" />Procedure Library
        </button>
        <button onClick={() => setActiveTab('book')}
          className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all ${activeTab === 'book' ? 'bg-white text-[#063D2F] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          data-testid="tab-book">
          <Activity className="w-3 h-3 inline mr-1.5" />Knowledge Book
        </button>
      </div>

      {activeTab === 'procedures' ? (
        <ProcedureLibraryTab />
      ) : activeTab === 'book' ? (
        <KnowledgeBookTab />
      ) : (
        <>
          <div className="flex items-center gap-2">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by condition..."
                className="pl-8 h-8 text-xs rounded-full" data-testid="protocol-search" />
            </div>
            <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-600">{protocols.length} protocol{protocols.length !== 1 ? 's' : ''}</Badge>
          </div>

          <InsightsPanel onProtocolUpdated={fetchProtocols} />
          <SkuSuggestionsPanel onProtocolUpdated={fetchProtocols} />

          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
          ) : protocols.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-8 text-center space-y-2">
                <Shield className="w-8 h-8 text-slate-300 mx-auto" />
                <p className="text-sm text-muted-foreground">No protocols defined yet</p>
                <p className="text-xs text-muted-foreground">Add treatment protocols so the AI agent can follow clinic-specific guidelines</p>
                <Button onClick={openCreate} variant="outline" className="rounded-full gap-1 text-xs mt-2" data-testid="empty-add-protocol">
                  <Plus className="w-3 h-3" /> Create First Protocol
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {protocols.map(p => <ProtocolCard key={p.id} protocol={p} onEdit={openEdit} onDelete={handleDelete} />)}
            </div>
          )}
        </>
      )}

      <ProtocolFormModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditingProtocol(null); }}
        onSave={editingProtocol ? handleUpdate : handleCreate}
        protocol={editingProtocol}
      />
    </div>
  );
}
